import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isExpiredDate, pickShortLink, requestShortLinks } from "./shortLinks";

// After a failed attempt the long link is used and nothing is re-requested for this long, so a
// backend outage cannot turn every keystroke / re-render into a request storm. A later click
// (or edit) after this window tries again.
const RETRY_AFTER_FAILURE_MS = 15 * 1000;

// pending bookkeeping: a URL is "pending" while at least one reason (debounce, request) holds it.
const hold = (state, url, delta) => {
  const next = (state.pending.get(url) || 0) + delta;
  if (next > 0) state.pending.set(url, next);
  else state.pending.delete(url);
};

const normalise = (items) => {
  const wanted = [];
  const seen = new Set();
  for (const item of items || []) {
    const url = item && item.url;
    if (typeof url !== "string" || !url || seen.has(url)) continue;
    seen.add(url);
    wanted.push({ url, expiryDate: (item && item.expiryDate) || "" });
  }
  return wanted;
};

/**
 * Short links for the share dialogs.
 *
 * Everything is keyed by the LONG share URL (never by a row/array index - see shortLinks.js):
 *
 *   linkFor(url)    the short link for that long URL when we have one, else the long URL itself
 *   shortFor(url)   { shortCode, shortUrl } or null
 *   isPending(url)  a short link for that URL is being waited for (debouncing or in flight);
 *                   copy/QR buttons use it so nobody copies the long link a moment too early
 *   failureFor(url) why the last attempt for that URL gave no short link ({ kind, status?, detail? },
 *                   see shortLinks.js) or null - what the share dialogs show next to the link
 *   seed(entries)   remember short links already stored on SharedFile.json entries ({url, shortCode, shortUrl})
 *   ensure(items)   Promise<Map<url, link>> - create/look up now (items: [{url, expiryDate}]).
 *                   A share whose expiryDate is already past is not asked about at all (it can
 *                   never get a short link, and one it had no longer opens): failureFor(url)
 *                   is then { kind: "expired" } - even if a short link is known for it.
 *   retry(item)     ensure() for one item right now, ignoring the back-off after a failure
 *   schedule(slot, item)  debounced ensure for one row; typing replaces the previous request of
 *                   the same slot, so a link is created once the user pauses - not per keystroke
 *   cancel(slot)    drop a row's scheduled request
 *
 * Every failure degrades to "use the long link": sharing never depends on this service. The
 * degrade is visible (failureFor), never silent.
 */
export default function useShortLinks({ http, baseUrl, token, folderName = "", debounceMs = 700 } = {}) {
  const [, setVersion] = useState(0);

  const stateRef = useRef(null);
  if (stateRef.current === null) {
    stateRef.current = {
      alive: true,
      links: new Map(), // longUrl -> { shortCode, shortUrl }
      pending: new Map(), // longUrl -> number of reasons it is pending (debounce, request)
      failedAt: new Map(), // longUrl -> timestamp of the last failed attempt
      failures: new Map(), // longUrl -> why the last attempt failed ({ kind, ... })
      inflight: new Map(), // longUrl -> Promise of the request that covers it
      timers: new Map(), // slot -> { timer, url }
    };
  }

  // Async callbacks must see the latest props, not the ones of the render that created them.
  const configRef = useRef({});
  configRef.current = { http, baseUrl, token, folderName, debounceMs };

  const notify = useCallback(() => {
    if (stateRef.current.alive) setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    const s = stateRef.current;
    s.alive = true;
    return () => {
      s.alive = false;
      s.timers.forEach(({ timer, url }) => {
        clearTimeout(timer);
        hold(s, url, -1);
      });
      s.timers.clear();
    };
  }, []);

  const seed = useCallback(
    (entries) => {
      const s = stateRef.current;
      let changed = false;
      for (const entry of entries || []) {
        const link = pickShortLink(entry);
        if (!link || typeof entry.url !== "string" || !entry.url || s.links.has(entry.url)) continue;
        s.links.set(entry.url, link);
        changed = true;
      }
      if (changed) notify();
    },
    [notify]
  );

  const ensure = useCallback(
    async (items) => {
      const s = stateRef.current;
      const wanted = normalise(items);

      const toFetch = [];
      const joined = [];
      let markedExpired = false;
      for (const item of wanted) {
        // A share that is already over never gets a short link (the server would answer "expired"
        // and store nothing), and one it got earlier no longer opens: do not ask, just remember
        // that it is over, for the dialog to say so - whether or not a link is known for it.
        if (isExpiredDate(item.expiryDate)) {
          const known = s.failures.get(item.url);
          if (!known || known.kind !== "expired") {
            s.failures.set(item.url, { kind: "expired" });
            markedExpired = true;
          }
          continue;
        }
        if (s.links.has(item.url)) continue; // already known
        const running = s.inflight.get(item.url);
        if (running) {
          joined.push(running); // someone is already creating it: wait for that request
          continue;
        }
        const failedAt = s.failedAt.get(item.url);
        if (failedAt && Date.now() - failedAt < RETRY_AFTER_FAILURE_MS) continue; // recently failed
        toFetch.push(item);
      }
      if (markedExpired) notify();

      if (toFetch.length > 0) {
        const cfg = configRef.current;
        toFetch.forEach(({ url }) => hold(s, url, +1));
        notify();
        const reasons = new Map();
        const run = requestShortLinks({
          http: cfg.http,
          baseUrl: cfg.baseUrl,
          token: cfg.token,
          items: toFetch,
          folderName: cfg.folderName,
          failures: reasons,
        }).then((found) => {
          toFetch.forEach(({ url }) => {
            s.inflight.delete(url);
            hold(s, url, -1);
            const link = found.get(url);
            if (link) {
              s.links.set(url, link);
              s.failedAt.delete(url);
              s.failures.delete(url);
            } else {
              s.failedAt.set(url, Date.now());
              s.failures.set(url, reasons.get(url) || { kind: "unknown" });
            }
          });
          notify();
        });
        toFetch.forEach(({ url }) => s.inflight.set(url, run));
        joined.push(run);
      }

      await Promise.all(joined);

      const result = new Map();
      wanted.forEach(({ url }) => {
        const link = s.links.get(url);
        if (link) result.set(url, link);
      });
      return result;
    },
    [notify]
  );

  const retry = useCallback(
    (item) => {
      const s = stateRef.current;
      const url = item && item.url;
      if (typeof url !== "string" || !url) return Promise.resolve(new Map());
      s.failedAt.delete(url); // an explicit "try again" is not subject to the back-off
      return ensure([item]);
    },
    [ensure]
  );

  const cancel = useCallback(
    (slot) => {
      const s = stateRef.current;
      const entry = s.timers.get(slot);
      if (!entry) return;
      clearTimeout(entry.timer);
      s.timers.delete(slot);
      hold(s, entry.url, -1);
      notify();
    },
    [notify]
  );

  const schedule = useCallback(
    (slot, item) => {
      const s = stateRef.current;
      cancel(slot); // typing again replaces this slot's earlier request

      const url = item && item.url;
      if (typeof url !== "string" || !url) return;

      if (isExpiredDate(item.expiryDate)) {
        ensure([item]).catch(() => {}); // nothing to wait for: it only notes that the share is over
        return;
      }
      if (s.links.has(url)) return;

      hold(s, url, +1);
      notify();
      const timer = setTimeout(() => {
        s.timers.delete(slot);
        ensure([item]).catch(() => {}); // starts the request (and its own hold) synchronously...
        hold(s, url, -1); // ...so there is no gap in isPending() when the debounce hold is released
        notify();
      }, configRef.current.debounceMs);
      s.timers.set(slot, { timer, url });
    },
    [cancel, ensure, notify]
  );

  return useMemo(
    () => ({
      seed,
      ensure,
      retry,
      schedule,
      cancel,
      shortFor: (url) => stateRef.current.links.get(url) || null,
      linkFor: (url) => {
        const link = stateRef.current.links.get(url);
        return (link && link.shortUrl) || url || "";
      },
      isPending: (url) => (stateRef.current.pending.get(url) || 0) > 0,
      failureFor: (url) => {
        const s = stateRef.current;
        const failure = s.failures.get(url) || null;
        if (failure && failure.kind === "expired") return failure; // over: its link is dead, short or long
        return s.links.has(url) ? null : failure;
      },
    }),
    [seed, ensure, retry, schedule, cancel]
  );
}
